import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlProcedureAddEditProps } from '../../interfaces/customControlProcedureAddEditProps';
import type { IKsyosDesktopActionLabels } from '../../utils/v1/AssetDropzoneWithActions';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, HeaderDivider, InputTextArea } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { CardioTypes } from '../../TCCr/v1/cardioTypes';
import { RustEcgAddEdit } from '../../TCCr/v1/RustEcgAddEdit';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { GraderSelector } from '../../utils/v1/GraderSelector';
import { NewOrEditProcedureProcessContainer } from '../../utils/v1/NewOrEditProcedureProcessContainer';
import { ServiceOrganizationConsumerSelector } from '../../utils/v1/ServiceOrganizationConsumerSelector';
import { TcorFollowingSteps } from './TcorFollowingSteps';

interface IInnerTcorV1ProcedureAddEditState {
    buttonDisabled?: boolean;
    isProcessing?: boolean;
    proceedWithoutFiles?: boolean;
    showProceedWithoutFiles?: boolean;
    startDesktop: boolean;
}

export function TcorV1ProcedureAddEdit(props: ICustomControlProcedureAddEditProps) {
    const episodeContext = useEpisodeContext();

    return (
        <InnerTcorV1ProcedureAddEdit
            {...props}
            {...episodeContext}
        />
    );
}

class InnerTcorV1ProcedureAddEdit extends Component<
    ICustomControlProcedureAddEditProps & IEpisodeContext,
    IInnerTcorV1ProcedureAddEditState
> {
    constructor(props: ICustomControlProcedureAddEditProps & IEpisodeContext) {
        super(props);
        this.state = {
            startDesktop: false,
        };
    }

    public render() {
        return (
            <NewOrEditProcedureProcessContainer
                currentProcedure={this.props.currentProcedure}
                isBioGrader={this.props.isBioGrader}
                isFirstItem={this.props.isFirstItem}
                process={this.props.process}
                service={this.props.service}
            >
                {this.addEditForm()}
            </NewOrEditProcedureProcessContainer>
        );
    }

    public addEditForm() {
        return (
            <>
                <Form
                    onSubmit={async () => null}
                    showOverlay={this.state.isProcessing}
                >
                    <CareProviderSelector
                        {...this.props}
                        entity={this.props.procedure}
                    />

                    <ServiceOrganizationConsumerSelector {...this.props} />

                    <FormDivider />

                    {!this.state.startDesktop && (
                        <AssetDropzoneWithActions
                            assets={this.props.procedure.assets}
                            assetsBaseUrl={FilesUrlBase.procedures}
                            cleanDropzoneAfterCompletingFiles={this.props.isAutoSaveActive}
                            headerText={getServiceLabel(
                                this.props.service.code,
                                'procedureAttachment',
                                translateKey('procedure.procedure')
                            )}
                            ksyosDesktopActionLabels={this.getKsyosDesktopActionLabels()}
                            onChange={async (assets) => await this.props.handleChange('assets', assets)}
                            onClickStartKsyosDesktop={async () => {
                                await this.setState({ startDesktop: true });
                            }}
                            onUploading={async (isUploading) => await this.setState({ buttonDisabled: isUploading })}
                            processActivityId={this.props.procedure.id}
                            service={this.props.service}
                        />
                    )}

                    <RustEcgAddEdit
                        afterStartDesktop={async () => await this.setState({ startDesktop: false })}
                        cardioType={CardioTypes.restEcgProcedure}
                        customControlData={this.props.procedure.customControlData}
                        employmentId={this.props.employmentId}
                        errors={this.props.errors}
                        handleChange={this.props.handleChangeCustomControl}
                        isDirectProcedureAdded={this.props.request.isDirectProcedureAdded}
                        organizationId={this.props.organizationId}
                        patientId={this.props.patient.id}
                        procedureId={this.props.procedure.id}
                        requestId={this.props.request.id}
                        saveAsDraft={async () => await this.saveForStartRustEcg()}
                        serviceId={this.props.service.id}
                        serviceProvider={this.props.serviceProvider}
                        setIsProcessing={async (isProcessing) => await this.setState({ isProcessing })}
                        startDesktop={this.state.startDesktop}
                        validateOne={this.props.validateOneCustomControl}
                    />

                    <FormDivider />

                    <HeaderDivider text={translateKey('common.remarks')} />
                    <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                        <FormLabel>{translateKey('common.remark')}</FormLabel>
                        <InputContainer>
                            <InputTextArea
                                automation="remark"
                                error={this.props.errors.get('remark')}
                                onChange={async (value) => await this.props.handleChange('remark', value)}
                                onDoValidate={async () => await this.props.validateOne('remark')}
                                placeholder={translateKey('common.remarksOrQuestions')}
                                value={this.props.procedure.remark}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <GraderSelector
                        {...this.props}
                        careProviderAndRequesterAndSelectedProvider={{ ...this.props.procedure }}
                        procedurePerformerOrganizationId={this.props.organizationId}
                    />

                    {!this.props.isBioGrader ? <TcorFollowingSteps isPractice /> : null}

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.procedure}
                                isExisting={!!this.props.request.id}
                                revertChanges={this.props.revertProcedureChanges}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveProcedure('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveProcedure('final');
                                }}
                            />,
                        ]}
                        hideButtons={this.props.isBioGrader}
                    />

                    {this.state.showProceedWithoutFiles && (
                        <ProceedWithoutFiles
                            entity={ProcessEntity.procedure}
                            onNo={async () =>
                                await this.setState({ showProceedWithoutFiles: false, buttonDisabled: false })
                            }
                            onYes={async () => {
                                await this.setState({ showProceedWithoutFiles: false, proceedWithoutFiles: true });
                                await this.saveProcedure('final');
                            }}
                            serviceType={this.props.service.serviceType}
                        />
                    )}
                </Form>
            </>
        );
    }

    private getKsyosDesktopActionLabels(): IKsyosDesktopActionLabels | undefined {
        if (this.props.serviceProvider && this.props.serviceProvider.usingRestEcgMeter) {
            return {
                titleWhenOnlyAction: translateKey('upload.orStartTheCardiograph'),
                linkTextWhenOnlyAction: translateKey('upload.viaKsyosDesktop'),
                linkTextWhenMultipleActions: translateKey('upload.startRestEcg'),
            };
        }

        return undefined;
    }

    private async saveProcedure(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedProcedure = await this.props.validateProcedure(saveType);
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (
            !this.state.proceedWithoutFiles &&
            saveType === 'final' &&
            this.props.procedure.assets.length === 0 &&
            (!this.props.procedure.customControlData.files || this.props.procedure.customControlData.files.length === 0)
        ) {
            return await this.setState({ showProceedWithoutFiles: true });
        }

        const requestId = await this.props.onSaveProcedure(saveType);
        await this.setState({ buttonDisabled: false });
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.procedureSubmitted',
                this.props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.props.onSave(requestId);
    }

    private async saveForStartRustEcg() {
        await this.setState({ buttonDisabled: true });

        const validatedProcedure = await this.props.validateProcedure('draft');
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const requestId = await this.props.onSaveProcedure('draft');
        await this.setState({ buttonDisabled: false });

        return requestId;
    }
}
